import { buildTree } from '../notes/folderTree';
import type { FolderNode } from '../notes/folderTree';
import type { Tables } from '../../types/database.types';

export type ResourceCategory = Tables<'resource_categories'>;
export type ResourceLink = Tables<'resource_links'>;
export type SharedAsset = Tables<'shared_assets'>;
export type SharedAssetFolder = Tables<'shared_asset_folders'>;

/**
 * The Resource Directory's pure lenses: which platform a link points at, and
 * how links group into sections.
 *
 * The platform is DERIVED from the URL rather than stored. A stored icon is a
 * second fact about the same link that nothing keeps in sync — correct a
 * Canva URL to a Figma one and the badge would go on saying Canva until
 * somebody noticed. Deriving it means the badge cannot disagree with the link
 * it sits on, and adding a platform later is one entry in this table rather
 * than a data migration.
 */

export interface PlatformBadge {
  key: string;
  label: string;
  tone: 'brand' | 'success' | 'warning' | 'neutral';
}

const UNKNOWN: PlatformBadge = { key: 'link', label: 'Link', tone: 'neutral' };

/**
 * Matched on the HOST, not the whole URL. Matching anywhere in the string
 * would badge `example.com/why-we-left-notion` as Notion.
 */
const PLATFORMS: { key: string; label: string; tone: PlatformBadge['tone']; hosts: string[] }[] = [
  { key: 'canva', label: 'Canva', tone: 'brand', hosts: ['canva.com'] },
  {
    key: 'sheets',
    label: 'Google Sheets',
    tone: 'success',
    hosts: ['docs.google.com/spreadsheets'],
  },
  { key: 'docs', label: 'Google Docs', tone: 'brand', hosts: ['docs.google.com/document'] },
  {
    key: 'slides',
    label: 'Google Slides',
    tone: 'warning',
    hosts: ['docs.google.com/presentation'],
  },
  { key: 'forms', label: 'Google Forms', tone: 'neutral', hosts: ['docs.google.com/forms'] },
  { key: 'drive', label: 'Google Drive', tone: 'success', hosts: ['drive.google.com'] },
  { key: 'notion', label: 'Notion', tone: 'neutral', hosts: ['notion.so', 'notion.site'] },
  { key: 'figma', label: 'Figma', tone: 'brand', hosts: ['figma.com'] },
  { key: 'slack', label: 'Slack', tone: 'warning', hosts: ['slack.com'] },
  { key: 'asana', label: 'Asana', tone: 'warning', hosts: ['asana.com'] },
  { key: 'airtable', label: 'Airtable', tone: 'brand', hosts: ['airtable.com'] },
  { key: 'loom', label: 'Loom', tone: 'brand', hosts: ['loom.com'] },
  { key: 'youtube', label: 'YouTube', tone: 'warning', hosts: ['youtube.com', 'youtu.be'] },
  { key: 'shopify', label: 'Shopify', tone: 'success', hosts: ['shopify.com', 'myshopify.com'] },
  { key: 'hubspot', label: 'HubSpot', tone: 'warning', hosts: ['hubspot.com'] },
];

export function platformOf(url: string): PlatformBadge {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // A link that will not parse gets the neutral badge rather than throwing
    // and taking the whole directory down with it.
    return UNKNOWN;
  }

  // Google puts five different products on one host, so the path decides
  // between them. Host plus first path segment, nothing further.
  const host = parsed.hostname.replace(/^www\./, '');
  const segment = parsed.pathname.split('/').filter((part) => part !== '')[0] ?? '';
  const haystack = segment === '' ? host : `${host}/${segment}`;

  for (const platform of PLATFORMS) {
    for (const candidate of platform.hosts) {
      const [candidateHost = '', candidateSegment] = candidate.split('/');
      const hostMatches = host === candidateHost || host.endsWith(`.${candidateHost}`);
      if (!hostMatches) {
        continue;
      }
      if (candidateSegment === undefined || haystack === candidate) {
        return { key: platform.key, label: platform.label, tone: platform.tone };
      }
    }
  }
  return UNKNOWN;
}

/** What the host reads as when there is no badge worth showing. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export const UNCATEGORISED_ID = 'uncategorised';
export const UNCATEGORISED_NAME = 'Uncategorised';
export const UNFILED_ID = 'unfiled';
export const UNFILED_NAME = 'Unfiled';

export interface ResourceSection {
  id: string;
  name: string;
  colorKey: string;
  links: ResourceLink[];
}

/**
 * Links grouped into their sections, in category order then link order.
 *
 * Uncategorised comes LAST and only when it has something in it: it is a
 * holding pen, not a destination, and an always-visible empty one would
 * suggest otherwise. Pinned links sort to the top within their section.
 */
export function groupLinks(
  categories: ResourceCategory[],
  links: ResourceLink[],
): ResourceSection[] {
  const ordered = categories
    .slice()
    .sort((left, right) => left.position - right.position || left.name.localeCompare(right.name));

  const sections: ResourceSection[] = ordered.map((category) => ({
    id: category.id,
    name: category.name,
    colorKey: category.color_key,
    links: sortLinks(links.filter((link) => link.category_id === category.id)),
  }));

  const known = new Set(ordered.map((category) => category.id));
  const loose = sortLinks(
    links.filter((link) => link.category_id === null || !known.has(link.category_id)),
  );
  if (loose.length > 0) {
    sections.push({
      id: UNCATEGORISED_ID,
      name: UNCATEGORISED_NAME,
      colorKey: 'neutral',
      links: loose,
    });
  }
  return sections;
}

function sortLinks(links: ResourceLink[]): ResourceLink[] {
  return links.slice().sort((left, right) => {
    if (left.is_pinned !== right.is_pinned) {
      return left.is_pinned ? -1 : 1;
    }
    return left.display_order - right.display_order || left.title.localeCompare(right.title);
  });
}

/**
 * The instant search. Matches title, description, platform label and host, so
 * typing "canva" finds every Canva link whether or not anybody wrote the word
 * in the title.
 */
export function matchesQuery(link: ResourceLink, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  const haystack = [
    link.title,
    link.description,
    platformOf(link.url).label,
    hostOf(link.url),
    link.url,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

/** Sections with their links narrowed by the search, empties dropped. */
export function filterSections(sections: ResourceSection[], query: string): ResourceSection[] {
  if (query.trim() === '') {
    return sections;
  }
  return sections
    .map((section) => ({ ...section, links: section.links.filter((l) => matchesQuery(l, query)) }))
    .filter((section) => section.links.length > 0);
}

/** "Added by Nez · 14 Aug 2026", or just the date when the name is unknown. */
export function attribution(name: string, iso: string): string {
  const when = new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const who = name.trim();
  return who === '' ? `Added ${when}` : `Added by ${who} · ${when}`;
}

// ── Nesting (Batch 11c) ────────────────────────────────────────────────────

/**
 * A section, its links, and every section inside it.
 *
 * The tree walk itself comes from the notes feature rather than being written
 * again here: buildTree, descendantIds and visibleRows are about a parent_id
 * column and nothing about notes, they are already tested, and two copies of a
 * cycle-safe tree walk is one copy too many.
 */
export interface ResourceNode {
  id: string;
  name: string;
  colorKey: string;
  depth: number;
  links: ResourceLink[];
  children: ResourceNode[];
  /** Links in this section and everything under it — what the count shows. */
  totalLinks: number;
}

function nodeFrom(
  folder: FolderNode,
  byId: Map<string, ResourceCategory>,
  linksByCategory: Map<string, ResourceLink[]>,
): ResourceNode {
  const children = folder.children.map((child) => nodeFrom(child, byId, linksByCategory));
  const links = sortLinks(linksByCategory.get(folder.id) ?? []);
  return {
    id: folder.id,
    name: folder.name,
    colorKey: byId.get(folder.id)?.color_key ?? 'neutral',
    depth: folder.depth,
    links,
    children,
    // Rolled up, so a collapsed section still says how much is inside it.
    // A count of the section's own links would read as 0 for every parent
    // whose material actually lives one level down.
    totalLinks: links.length + children.reduce((sum, child) => sum + child.totalLinks, 0),
  };
}

/**
 * The directory as a tree.
 *
 * Uncategorised is NOT part of it — it is appended by the page as a flat
 * section, because a holding pen with children would suggest somebody meant to
 * organise it that way.
 */
export function buildResourceTree(
  categories: ResourceCategory[],
  links: ResourceLink[],
): ResourceNode[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const linksByCategory = new Map<string, ResourceLink[]>();
  for (const link of links) {
    if (link.category_id === null || !byId.has(link.category_id)) {
      continue;
    }
    const bucket = linksByCategory.get(link.category_id) ?? [];
    bucket.push(link);
    linksByCategory.set(link.category_id, bucket);
  }
  const flat = categories.map((category) => ({
    id: category.id,
    name: category.name,
    parent_id: category.parent_id,
  }));
  return buildTree(flat).map((folder) => nodeFrom(folder, byId, linksByCategory));
}

/** The links belonging to no live section, sorted. Empty when there are none. */
export function looseLinks(categories: ResourceCategory[], links: ResourceLink[]): ResourceLink[] {
  const known = new Set(categories.map((category) => category.id));
  return sortLinks(
    links.filter((link) => link.category_id === null || !known.has(link.category_id)),
  );
}

/**
 * Whether a proposed section name can be used here, or why not.
 *
 * Siblings only, matching the two partial unique indexes exactly. Comparing
 * against every category in the workspace would refuse a perfectly legal
 * second "Templates" under a different parent — which is the whole reason
 * nesting was added.
 */
export function categoryNameProblem(
  name: string,
  categories: ResourceCategory[],
  parentId: string | null,
  ignoreId: string | null = null,
): string | null {
  const clean = name.trim();
  if (clean === '') {
    return 'Give the section a name.';
  }
  const folded = clean.toLowerCase();
  const clash = categories.some(
    (category) =>
      category.id !== ignoreId &&
      (category.parent_id ?? null) === parentId &&
      category.name.trim().toLowerCase() === folded,
  );
  return clash ? 'A section here already has that name.' : null;
}

/**
 * Whether this person may take a shared asset off the list.
 *
 * Mirrors shared_assets_delete exactly: the uploader, or a TAP admin
 * (20260922000100_resource_directory.sql). Same test LinkCard uses for links.
 * Showing Delete to anyone else would only produce a refusal they cannot act on.
 */
export function mayRemoveSharedAsset(
  viewer: { id: string; internal_role: string | null } | null,
  asset: { uploaded_by: string },
): boolean {
  return (
    viewer !== null && (asset.uploaded_by === viewer.id || viewer.internal_role === 'tap_admin')
  );
}

/** One line for a multi-file upload, naming what did not go up. Empty when all did. */
export function uploadFailureMessage(failed: string[]): string {
  if (failed.length === 0) {
    return '';
  }
  const count = failed.length;
  return `${String(count)} file${count === 1 ? '' : 's'} did not upload: ${failed.join(', ')}. Try ${count === 1 ? 'it' : 'them'} again.`;
}

export function sortFolders(folders: SharedAssetFolder[]): SharedAssetFolder[] {
  return folders
    .slice()
    .sort((left, right) =>
      left.name.trim().localeCompare(right.name.trim(), undefined, { sensitivity: 'accent' }),
    );
}

export function assetsInFolder(assets: SharedAsset[], folderId: string | null): SharedAsset[] {
  return assets.filter((asset) => (asset.folder_id ?? null) === folderId);
}

export function folderCounts(assets: SharedAsset[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const asset of assets) {
    const id = asset.folder_id ?? UNFILED_ID;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

export function folderNameProblem(name: string, folders: SharedAssetFolder[]): string | null {
  const clean = name.trim();
  if (clean === '') {
    return 'Give the folder a name.';
  }
  return folders.some((folder) => folder.name.trim().toLowerCase() === clean.toLowerCase())
    ? 'A folder with that name already exists.'
    : null;
}

export function findFolderByName(
  folders: SharedAssetFolder[],
  name: string,
): SharedAssetFolder | undefined {
  const clean = name.trim().toLowerCase();
  return folders.find((folder) => folder.name.trim().toLowerCase() === clean);
}

export function mayDeleteFolder(viewer: { internal_role: string | null } | null): boolean {
  return viewer !== null && viewer.internal_role === 'tap_admin';
}

export function topFolderName(files: File[]): string {
  return files[0]?.webkitRelativePath.split('/')[0] ?? '';
}

export function folderUploadFiles(files: File[]): File[] {
  return files.filter((file) => !file.name.startsWith('.'));
}

export function deleteFolderWarning(folder: SharedAssetFolder, fileCount: number): string {
  return `Delete the folder "${folder.name}"? Its ${String(fileCount)} file${fileCount === 1 ? '' : 's'} go to Unfiled. Nothing is deleted from Google Drive.`;
}

/** What deleting this section will do, said before anybody presses it. */
export function deleteWarning(node: ResourceNode): string {
  const subsections = node.children.length;
  const linkCount = node.totalLinks;
  const parts: string[] = [];
  if (subsections > 0) {
    parts.push(`${String(subsections)} subsection${subsections === 1 ? '' : 's'} will be deleted`);
  }
  if (linkCount > 0) {
    parts.push(`${String(linkCount)} link${linkCount === 1 ? '' : 's'} will move to Uncategorised`);
  }
  if (parts.length === 0) {
    return `Delete "${node.name}"?`;
  }
  return `Delete "${node.name}"? ${parts.join(', and ')}.`;
}
